import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useForm, type UseFormRegister } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  ExceptionReportScope,
  GovernanceProjectTypeValues,
  ProjectDeliveryMode,
  ProjectDeliveryModeValues,
  ProjectGovernanceState,
  ProjectGovernanceSubmitKind,
  RoleOptions,
  type GovernanceProjectType,
  type ProjectDto,
  type ProjectGovernanceDto,
} from '@paladin-monorepo/common';
import { Textarea } from '@/components/ui/textarea';
import { GovernanceRequestCard } from './GovernanceRequestCard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/lib/auth';
import { isInquirerAccount, isPrince2Account } from '@/lib/permissions';
import { projectsApi } from '@/lib/api/projects';
import { projectGovernanceApi } from '@/lib/api/project-governance';
import { governanceQueryKey, useGovernanceMutation } from './useGovernanceMutation';
import { GovernanceRolesForm } from './GovernanceRolesForm';
import { GovernanceStagesForm } from './GovernanceStagesForm';
import { GovernanceMilestonesSection } from './GovernanceMilestonesSection';
import { GovernanceDecisionForm } from './GovernanceDecisionForm';
import { ChangeAuthorityForm } from './ChangeAuthorityForm';
import { CommunicationPlanForm } from './CommunicationPlanForm';
import { ProjectMonitoringCard } from './ProjectMonitoringCard';
import { ExceptionReportForm } from './ExceptionReportForm';
import { formatPrice } from '@/utils/formatPrice';
import { Input } from '@/components/ui/input';

const MODE_EDITABLE_STATES: ProjectGovernanceState[] = [
  ProjectGovernanceState.Preparation,
  ProjectGovernanceState.AwaitingInitiationAuthorization,
  ProjectGovernanceState.Initiation,
];

const BASELINE_EDITABLE_STATES: ProjectGovernanceState[] = [
  ...MODE_EDITABLE_STATES,
  ProjectGovernanceState.AwaitingProjectAuthorization,
];

/** The request that the Project Manager sends again from a waiting state. */
const RESUBMIT_KIND: Partial<Record<ProjectGovernanceState, ProjectGovernanceSubmitKind>> = {
  [ProjectGovernanceState.AwaitingInitiationAuthorization]: ProjectGovernanceSubmitKind.Initiation,
  [ProjectGovernanceState.AwaitingProjectAuthorization]: ProjectGovernanceSubmitKind.Project,
  [ProjectGovernanceState.AwaitingNextStageAuthorization]: ProjectGovernanceSubmitKind.StageEnd,
  [ProjectGovernanceState.ReadyForClosure]: ProjectGovernanceSubmitKind.StageEnd,
};

const SUMMARY_MAX = 8000;

export function ProjectGovernancePanel({ project }: { project: ProjectDto }) {
  const { t } = useTranslation();
  const { user } = useAuth({ middleware: 'auth', tmp: 'ProjectGovernancePanel' });
  const isCustomer = isInquirerAccount(user ?? undefined);
  const prince2 = isPrince2Account(user);

  // Supplier users follow the customer setting: the API returns 403 when PRINCE2 is off.
  const { data: governance, isLoading, isError } = useQuery({
    queryKey: governanceQueryKey(project.id),
    queryFn: () => projectGovernanceApi.get(project.id),
    enabled: !isCustomer || prince2,
    retry: false,
  });
  const { data: participants = [] } = useQuery({
    queryKey: ['project-comment-participants', project.id],
    enabled: !!governance,
    queryFn: () => projectsApi.listCommentParticipants(project.id),
  });
  const nameOf = (userId: string | null) =>
    userId ? (participants.find((p) => p.id === userId)?.displayName ?? userId) : t('workspace.governance.roles.none');

  const openMutation = useGovernanceMutation(project.id, () => projectGovernanceApi.open(project.id));
  const canOpen =
    (prince2 && (user?.role === RoleOptions.ACCOUNT_OWNER || user?.role === RoleOptions.ACCOUNT_ADMIN)) ||
    user?.role === RoleOptions.SUPERADMIN;

  if (isLoading || isError) return null;
  if (!governance && !canOpen) return null;

  if (!governance) {
    return (
      <Card className="lg:col-span-3">
        <CardContent className="flex items-center justify-between gap-4 py-4">
          <div>
            <h3 className="font-medium">{t('workspace.governance.title')}</h3>
            <p className="text-sm text-muted-foreground">{t('workspace.governance.notOpen')}</p>
          </div>
          {canOpen && (
            <Button onClick={() => openMutation.mutate()} disabled={openMutation.isPending}>
              {t('workspace.governance.open')}
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="lg:col-span-3">
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          {t('workspace.governance.title')}
          <Badge variant="secondary">{t(`workspace.governance.state.${governance.state}`)}</Badge>
          {governance.deliveryMode && (
            <Badge variant="outline">{t(`workspace.governance.deliveryMode.${governance.deliveryMode}`)}</Badge>
          )}
          {governance.exception && <Badge variant="destructive">{t('workspace.governance.exception')}</Badge>}
          {governance.paused && <Badge variant="warning">{t('workspace.governance.paused')}</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="grid gap-6 lg:grid-cols-2">
        <section className="space-y-3">
          <h4 className="text-sm font-medium">{t('workspace.governance.roles.title')}</h4>
          <GovernanceRolesForm
            projectId={project.id}
            governance={governance}
            participants={participants}
            nameOf={nameOf}
          />
          <DeliveryModeField project={project} governance={governance} />
          <ProjectTypeField projectId={project.id} governance={governance} />
          <BaselineBudgetField projectId={project.id} currency={project.currency} governance={governance} />
          <ChangeAuthorityForm projectId={project.id} currency={project.currency} governance={governance} />
          <CommunicationPlanForm projectId={project.id} governance={governance} />
        </section>

        <section className="space-y-3">
          <h4 className="text-sm font-medium">{t('workspace.governance.nextStep.title')}</h4>
          {governance.pendingRequest && (
            <GovernanceRequestCard
              projectId={project.id}
              request={governance.pendingRequest}
              opinionRoles={governance.permissions.opinionRoles}
              nameOf={nameOf}
            />
          )}
          <NextStep projectId={project.id} governance={governance} />
          {canRecordDecision(governance) && governance.availableDecisions.length > 0 && (
            <GovernanceDecisionForm projectId={project.id} governance={governance} />
          )}
        </section>

        <section className="space-y-3">
          <h4 className="text-sm font-medium">{t('workspace.governance.stages.title')}</h4>
          <GovernanceStagesForm projectId={project.id} governance={governance} currency={project.currency} />
        </section>

        <section className="space-y-3">
          <h4 className="text-sm font-medium">{t('workspace.governance.milestones.sectionTitle')}</h4>
          <GovernanceMilestonesSection
            project={project}
            governance={governance}
            participants={participants}
            nameOf={nameOf}
          />
        </section>

        <ProjectMonitoringCard projectId={project.id} governance={governance} />

        {governance.approvals.length > 0 && (
          <section className="space-y-2 lg:col-span-2">
            <h4 className="text-sm font-medium">{t('workspace.governance.approvals.title')}</h4>
            <ul className="space-y-2 text-sm">
              {governance.approvals.map((a) => (
                <li key={a.id} className="border-b border-border/60 pb-2 last:border-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{t(`workspace.governance.decisionKind.${a.kind}`)}</span>
                    <Badge variant={a.decision === 'APPROVED' ? 'success' : 'destructive'}>
                      {t(`workspace.governance.decision.${a.decision}`)}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {nameOf(a.approverUserId)} · {new Date(a.createdAt).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-muted-foreground">{a.rationale}</p>
                </li>
              ))}
            </ul>
          </section>
        )}
      </CardContent>
    </Card>
  );
}

function DeliveryModeField({ project, governance }: { project: ProjectDto; governance: ProjectGovernanceDto }) {
  const { t } = useTranslation();
  const canEdit =
    (governance.permissions.isBusinessLayer || governance.permissions.isExecutive) &&
    MODE_EDITABLE_STATES.includes(governance.state);
  const mutation = useGovernanceMutation(project.id, (mode: ProjectDeliveryMode) =>
    projectGovernanceApi.setDeliveryMode(project.id, { mode, expectedVersion: governance.version }),
  );

  if (!canEdit) return null;

  return (
    <div className="space-y-1">
      <Label>{t('workspace.governance.deliveryMode.label')}</Label>
      <Select
        value={governance.deliveryMode ?? undefined}
        onValueChange={(mode) => mutation.mutate(mode as ProjectDeliveryMode)}
        disabled={mutation.isPending}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder={t('workspace.governance.deliveryMode.placeholder')} />
        </SelectTrigger>
        <SelectContent>
          {ProjectDeliveryModeValues.map((mode) => (
            <SelectItem
              key={mode}
              value={mode}
              disabled={mode === ProjectDeliveryMode.Internal && !!project.offerId}
            >
              {t(`workspace.governance.deliveryMode.${mode}`)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function ProjectTypeField({ projectId, governance }: { projectId: string; governance: ProjectGovernanceDto }) {
  const { t } = useTranslation();
  const { permissions } = governance;
  const canEdit =
    (permissions.isBusinessLayer || permissions.isExecutive || permissions.isProjectManager) &&
    governance.state !== ProjectGovernanceState.Closed;
  const mutation = useGovernanceMutation(projectId, (projectType: GovernanceProjectType) =>
    projectGovernanceApi.setProjectType(projectId, { projectType }),
  );

  if (!canEdit) {
    return governance.projectType ? (
      <p className="text-sm">
        <span className="text-muted-foreground">{t('workspace.governance.projectType.label')}: </span>
        {t(`workspace.governance.projectType.${governance.projectType}`)}
      </p>
    ) : null;
  }

  return (
    <div className="space-y-1">
      <Label>{t('workspace.governance.projectType.label')}</Label>
      <Select
        value={governance.projectType ?? undefined}
        onValueChange={(type) => mutation.mutate(type as GovernanceProjectType)}
        disabled={mutation.isPending}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder={t('workspace.governance.projectType.placeholder')} />
        </SelectTrigger>
        <SelectContent>
          {GovernanceProjectTypeValues.map((type) => (
            <SelectItem key={type} value={type}>
              {t(`workspace.governance.projectType.${type}`)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function BaselineBudgetField({
  projectId,
  currency,
  governance,
}: {
  projectId: string;
  currency: string;
  governance: ProjectGovernanceDto;
}) {
  const { t } = useTranslation();
  const canEdit =
    governance.permissions.isBusinessLayer && BASELINE_EDITABLE_STATES.includes(governance.state);
  const schema = yup
    .object({
      baselineBudget: yup
        .string()
        .defined()
        .test('budget', t('workspace.governance.baselineBudget.invalid'), (v) => !v || Number(v) >= 0),
    })
    .required();
  const { register, handleSubmit, formState } = useForm({
    resolver: yupResolver(schema),
    values: { baselineBudget: governance.baselineBudget === null ? '' : String(governance.baselineBudget) },
  });
  const mutation = useGovernanceMutation(projectId, (value: string) =>
    projectGovernanceApi.setBaselineBudget(projectId, {
      expectedVersion: governance.version,
      baselineBudget: value === '' ? null : Number(value),
    }),
  );

  if (!canEdit) {
    return governance.baselineBudget !== null ? (
      <p className="text-sm">
        <span className="text-muted-foreground">{t('workspace.governance.baselineBudget.label')}: </span>
        {formatPrice(governance.baselineBudget, currency)}
      </p>
    ) : null;
  }

  return (
    <form onSubmit={handleSubmit((v) => mutation.mutate(v.baselineBudget))} className="space-y-1">
      <Label htmlFor="baselineBudget">{t('workspace.governance.baselineBudget.label')}</Label>
      <div className="flex gap-2">
        <Input id="baselineBudget" type="number" min={0} step="0.01" {...register('baselineBudget')} />
        <span className="self-center text-sm text-muted-foreground">{currency}</span>
        <Button type="submit" size="sm" variant="outline" disabled={mutation.isPending}>
          {t('workspace.governance.baselineBudget.save')}
        </Button>
      </div>
      {formState.errors.baselineBudget && (
        <p className="text-xs text-destructive">{formState.errors.baselineBudget.message}</p>
      )}
      <p className="text-xs text-muted-foreground">{t('workspace.governance.baselineBudget.hint')}</p>
    </form>
  );
}

function NextStep({ projectId, governance }: { projectId: string; governance: ProjectGovernanceDto }) {
  const { t } = useTranslation();
  const { permissions, state, pendingRequest } = governance;
  const flagsAvailable = state !== ProjectGovernanceState.Preparation && state !== ProjectGovernanceState.Closed;
  const resubmitKind = !pendingRequest || pendingRequest.stale ? RESUBMIT_KIND[state] : undefined;
  const submitKind = governance.nextSubmit ?? resubmitKind;

  const schema = yup.object({ summary: yup.string().max(SUMMARY_MAX).defined() }).required();
  const { register, handleSubmit, reset, setValue } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { summary: '' },
  });
  const draftKind = submitKind ?? resubmitKind;
  const draftMutation = useMutation({
    mutationFn: () => projectGovernanceApi.draftSubmitSummary(projectId, { kind: draftKind! }),
    onSuccess: (result) => {
      if (result.draft) setValue('summary', result.draft, { shouldDirty: true });
    },
  });
  const submitMutation = useGovernanceMutation(
    projectId,
    (v: { kind: ProjectGovernanceSubmitKind; summary: string }) =>
      projectGovernanceApi.submit(projectId, {
        kind: v.kind,
        summary: v.summary.trim() || undefined,
        expectedVersion: governance.version,
      }),
    () => reset(),
  );
  const flagsMutation = useGovernanceMutation(projectId, (flags: { exception?: boolean; paused?: boolean }) =>
    projectGovernanceApi.setFlags(projectId, { ...flags, expectedVersion: governance.version }),
  );
  const onSubmit = handleSubmit((v) => submitKind && submitMutation.mutate({ kind: submitKind, summary: v.summary }));

  return (
    <div className="space-y-3">
      {resubmitKind && !governance.nextSubmit && (
        <>
          <p className="text-sm">
            {pendingRequest?.stale
              ? t('workspace.governance.nextStep.resubmitStale')
              : t('workspace.governance.nextStep.resubmitMissing')}
          </p>
          {permissions.isProjectManager && (
            <form className="space-y-2" onSubmit={onSubmit}>
              <SummaryField register={register} />
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  type="button"
                  variant="outline"
                  disabled={draftMutation.isPending}
                  onClick={() => draftMutation.mutate()}
                >
                  {draftMutation.isPending
                    ? t('workspace.governance.monitoring.draftSummaryLoading')
                    : t('workspace.governance.monitoring.draftSummary')}
                </Button>
                <Button size="sm" type="submit" disabled={submitMutation.isPending}>
                  {t('workspace.governance.nextStep.resubmit')}
                </Button>
              </div>
            </form>
          )}
        </>
      )}
      {governance.nextSubmit ? (
        <>
          <p className="text-sm">{t(`workspace.governance.submitKind.${governance.nextSubmit}`)}</p>
          {governance.missing.length > 0 ? (
            <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
              {governance.missing.map((m) => (
                <li key={m.code}>
                  {t(`workspace.governance.missing.${m.code}`, { defaultValue: m.code })}{' '}
                  <span className="text-xs">({t(`workspace.governance.owner.${m.owner}`)})</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">{t('workspace.governance.nextStep.ready')}</p>
          )}
          {permissions.isProjectManager && (
            <form className="space-y-2" onSubmit={onSubmit}>
              <SummaryField register={register} />
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  type="button"
                  variant="outline"
                  disabled={draftMutation.isPending || governance.missing.length > 0}
                  onClick={() => draftMutation.mutate()}
                >
                  {draftMutation.isPending
                    ? t('workspace.governance.monitoring.draftSummaryLoading')
                    : t('workspace.governance.monitoring.draftSummary')}
                </Button>
                <Button size="sm" type="submit" disabled={submitMutation.isPending || governance.missing.length > 0}>
                  {t('workspace.governance.nextStep.submit')}
                </Button>
              </div>
            </form>
          )}
        </>
      ) : (
        !resubmitKind && (
        <p className="text-sm text-muted-foreground">
          {state === ProjectGovernanceState.Closed
            ? t('workspace.governance.nextStep.closed')
            : t('workspace.governance.nextStep.waiting')}
        </p>
        )
      )}

      {flagsAvailable && permissions.isProjectManager && !governance.exception && (
        <ExceptionReportForm projectId={projectId} governance={governance} />
      )}
      {flagsAvailable && (
        <div className="flex flex-wrap gap-2">
          {permissions.isExecutive && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => flagsMutation.mutate({ paused: !governance.paused })}
              disabled={flagsMutation.isPending}
            >
              {governance.paused ? t('workspace.governance.resume') : t('workspace.governance.pause')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function canRecordDecision(governance: ProjectGovernanceDto): boolean {
  const projectException =
    governance.pendingRequest?.kind === ProjectGovernanceSubmitKind.ExceptionReport &&
    governance.pendingRequest.snapshot.exceptionReport?.scope === ExceptionReportScope.Project;
  if (projectException) return governance.permissions.isBusinessLayer;
  return governance.permissions.isExecutive;
}

function SummaryField({ register }: { register: UseFormRegister<{ summary: string }> }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-1">
      <Label htmlFor="governance-submit-summary">{t('workspace.governance.nextStep.summary')}</Label>
      <Textarea
        id="governance-submit-summary"
        rows={3}
        maxLength={SUMMARY_MAX}
        placeholder={t('workspace.governance.nextStep.summaryPlaceholder')}
        {...register('summary')}
      />
    </div>
  );
}
